import { Outlet, createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { z } from 'zod';

import { BranchSheetFromParams } from '@/components/Branches/BranchSheetFromParams';
import { LayoutDefault } from '@/components/LayoutDefault';
import { LayoutHeader } from '@/components/LayoutHeader';
import { LayoutHeaderTitle } from '@/components/LayoutHeaderTitle';
import { NewAssetAction } from '@/components/LibraryTable/NewAssetAction';
import { SlocWarning } from '@/components/LibraryTable/SlocWarning';
import { AddCodebaseButton } from '@/components/Sources/AddCodebaseButton';
import { CodebaseSlotsIndicator } from '@/components/Sources/CodebaseSlotsIndicator';
import { SourcesNav } from '@/components/Sources/SourcesNav';
import { useContextModeState } from '@/hooks/useContextMode';
import { useIsOrgOrSourceAdmin } from '@/hooks/useIsOrgOrSourceAdmin';
import { useUser } from '@/hooks/useUser';
import { searchParam } from '@/utils/router';

// Per-route header configuration. Child routes declare their own header via
// `staticData`; the layout reads the deepest match that provides a navbarTitle
// (the same pattern LayoutContext uses to read route staticData).
export interface DashboardStaticData {
  navbarTitle?: string;
}

const dashboardSearchSchema = z.object({
  branch: searchParam().optional(),
  branchCodebase: searchParam().optional(),
});

// Pathless layout route — the `_dashboard` segment is stripped from URLs, so
// children resolve at /$org/sources and /$org/branches. It
// provides the shared shell (LayoutDefault chrome + SourcesNav) and renders the
// active child via <Outlet/>.
export const Route = createFileRoute('/$organizationName/_dashboard')({
  component: DashboardLayout,
  validateSearch: dashboardSearchSchema,
});

function DashboardLayout() {
  const { branch, branchCodebase } = Route.useSearch();
  const navigate = useNavigate();
  // Users who can't manage sources get a read-only view: no add-source or
  // add-SLOC actions in the header.
  const isOrgOrSourceAdmin = useIsOrgOrSourceAdmin();
  const { isSuperAdmin } = useUser();
  // A LITE organization is billed by codebase slot, not SLOC, and adds
  // codebases through its Git provider rather than by upload. Its branch
  // detail sheet, each branch's context state, stays unmounted too, so a
  // saved `?branch=` URL neither opens it nor fires its request. The mode
  // reads as LEGACY until it is known, so both wait for the answer rather
  // than mount for a mode that generates and swap.
  const { isLoading: isModeLoading, mode } = useContextModeState();
  const isModeKnown = !isModeLoading;
  const isContextLight = mode === 'LITE';
  // LITE's actions (the slots indicator, Add codebase) are a super admin's
  // alone, since only they can connect a provider; a source admin gets no
  // header rather than an empty one.
  const hasHeaderActions = isOrgOrSourceAdmin && isModeKnown && (!isContextLight || isSuperAdmin);

  const header = useRouterState({
    select: (s) =>
      (s.matches.findLast((m) => (m.staticData as DashboardStaticData)?.navbarTitle)?.staticData ??
        {}) as DashboardStaticData,
  });

  return (
    <>
      <LayoutDefault
        showBorder
        navbarTitle={header.navbarTitle}
        header={
          <LayoutHeader
            right={
              hasHeaderActions ? (
                <div className="flex items-center gap-2.5">
                  {isContextLight ? (
                    <>
                      <CodebaseSlotsIndicator />
                      <AddCodebaseButton />
                    </>
                  ) : (
                    <>
                      <SlocWarning />
                      <NewAssetAction />
                    </>
                  )}
                </div>
              ) : undefined
            }
          >
            <LayoutHeaderTitle title={header.navbarTitle ?? ''} />
          </LayoutHeader>
        }
      >
        <div className="space-y-4 px-4 pt-4 pb-12">
          <SourcesNav />
          <Outlet />
        </div>
      </LayoutDefault>
      {isModeKnown && !isContextLight ? (
        <BranchSheetFromParams
          branch={branch}
          branchCodebase={branchCodebase}
          onClose={() =>
            navigate({ to: '.', search: (prev) => ({ ...prev, branch: undefined, branchCodebase: undefined }) })
          }
        />
      ) : null}
    </>
  );
}
